// EXIF orientation normalization for signed-URL uploads (REF-5982).
//
// Phone cameras store photos with the sensor's raw pixel layout and an EXIF orientation tag
// (1-8) telling viewers how to rotate/mirror on display. Browsers and galleries honor the tag,
// but our uploads PUT the raw bytes straight to GCS via a signed URL — the backend pipelines
// then process the sideways raw pixels and produce rotated outputs (studio full_look reference
// case: orientation=8 → output rotated 90°, crop coordinates shifted).
//
// `bakeExifOrientation` re-encodes such a JPEG through a canvas (the browser decodes it already
// rotated), producing a file whose pixels are upright and which carries no EXIF at all. Files
// without a rotating tag are returned untouched, so the common path has zero re-encode cost and
// zero quality loss. See docs/systems/upload-restrictions.md → "EXIF orientation normalization".

const EXIF_SCAN_BYTES = 128 * 1024;
const ORIENTATION_TAG = 0x0112;
const BAKED_JPEG_QUALITY = 0.92;

// jsdom (Jest) and very old Safari lack Blob.prototype.arrayBuffer
const blobToArrayBuffer = (blob: Blob): Promise<ArrayBuffer> => {
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
};

const readTiffOrientation = (view: DataView, tiffStart: number): number | null => {
  if (tiffStart + 8 > view.byteLength) return null;

  const endianTag = view.getUint16(tiffStart);
  const littleEndian = endianTag === 0x4949; // "II"; "MM" (0x4d4d) is big-endian
  if (!littleEndian && endianTag !== 0x4d4d) return null;
  if (view.getUint16(tiffStart + 2, littleEndian) !== 42) return null;

  const ifdStart = tiffStart + view.getUint32(tiffStart + 4, littleEndian);
  if (ifdStart + 2 > view.byteLength) return null;

  const entryCount = view.getUint16(ifdStart, littleEndian);
  for (let i = 0; i < entryCount; i++) {
    const entry = ifdStart + 2 + i * 12;
    if (entry + 12 > view.byteLength) return null;
    if (view.getUint16(entry, littleEndian) === ORIENTATION_TAG) {
      const value = view.getUint16(entry + 8, littleEndian);

      return value >= 1 && value <= 8 ? value : null;
    }
  }

  return null;
};

/**
 * Reads the EXIF orientation (1-8) from the leading bytes of a JPEG, or null when the stream is
 * not a JPEG, carries no EXIF APP1 segment, or has no orientation tag. Only the buffer passed in
 * is scanned — callers slice the first `EXIF_SCAN_BYTES` of the file, which always contains APP1
 * (EXIF must precede image data, and in practice sits within the first few KB).
 */
export const readJpegOrientation = (buffer: ArrayBuffer): number | null => {
  const view = new DataView(buffer);
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null;

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset);
    if ((marker & 0xff00) !== 0xff00) return null; // corrupt stream
    if (marker === 0xffda || marker === 0xffd9) return null; // SOS / EOI — no EXIF past here

    const size = view.getUint16(offset + 2); // includes the two length bytes themselves
    if (size < 2) return null;

    // APP1 with "Exif\0\0" payload → TIFF block follows
    if (marker === 0xffe1 && offset + 10 <= view.byteLength && view.getUint32(offset + 4) === 0x45786966 && view.getUint16(offset + 8) === 0x0000) {
      return readTiffOrientation(view, offset + 10);
    }

    offset += 2 + size;
  }

  return null;
};

// Both decode paths rely on the browser applying EXIF orientation itself (default behavior since
// Chrome 81 / Firefox 77 / Safari 13.1 — `image-orientation: from-image` became the CSS default).
const decodeOriented = async (file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; cleanup: () => void }> => {
  if (typeof createImageBitmap === "function") {
    try {
      // "from-image" postdates TS 4.6's DOM lib (its ImageOrientation union lacks the value), hence the cast
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" as any });

      return { source: bitmap, width: bitmap.width, height: bitmap.height, cleanup: () => bitmap.close() };
    } catch {
      // older Safari rejects the options bag — fall through to <img> decode
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to decode image for EXIF baking"));
      img.src = url;
    });

    return { source: image, width: image.naturalWidth, height: image.naturalHeight, cleanup: () => URL.revokeObjectURL(url) };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
};

/**
 * Returns an upload-ready Blob whose pixels are upright and which carries no EXIF orientation.
 *
 * - Non-JPEG files (PNG/WebP/ZIP/XLSX/PDF…) are returned as-is — JPEG is the only accepted
 *   format that carries a rotating orientation tag in practice.
 * - JPEGs with no tag or orientation=1 are returned as-is (no re-encode, no quality loss).
 * - JPEGs with orientation 2-8 are decoded (browser applies the rotation), drawn to a canvas,
 *   and re-encoded as JPEG. The result has upright pixels and no metadata.
 * - Fail-open: any decode/canvas failure returns the original file — a rotated upload beats a
 *   blocked one.
 */
export const bakeExifOrientation = async (file: File | Blob): Promise<Blob> => {
  try {
    if (file.type !== "image/jpeg") return file;

    const head = await blobToArrayBuffer(file.slice(0, EXIF_SCAN_BYTES));
    const orientation = readJpegOrientation(head);
    if (!orientation || orientation === 1) return file;

    const { source, width, height, cleanup } = await decodeOriented(file);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      cleanup();

      return file;
    }
    context.drawImage(source, 0, 0);
    cleanup();

    const baked = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", BAKED_JPEG_QUALITY));

    return baked ?? file;
  } catch {
    return file;
  }
};
